import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as confetti from 'canvas-confetti';
import { spookyConfetti } from 'src/shared/util/halloween';
import { take } from 'rxjs/operators';

import { Location } from '@angular/common';

import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';

import { FinancialConnectionsService } from 'src/shared/services/financial-connections.service';
import { SubscriptionsQueriesService } from 'src/shared/services/subscriptions-queries.service';
import { PosthogService } from 'src/shared/services/posthog.service';

import { AddressModel } from 'src/models/address.model';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { ThemeService } from 'src/shared/services/theme.service';
import { AddProsOnboardingComponent } from 'src/shared/components/onboarding/add-pros/add-pros.component';
import { ListingsSetupComponent } from 'src/shared/components/listings-setup/listings-setup.component';
import { ConnectBankOnboardingComponent } from 'src/shared/components/onboarding/connect-bank-onboarding/connect-bank-onboarding.component';
import { ManageLockAssignmentsComponent } from 'src/shared/components/manage-lock-assignments/manage-lock-assignments.component';
import { LegalComplianceOnboardingComponent } from 'src/shared/components/onboarding/legal-compliance-onboarding/legal-compliance-onboarding.component';
import { GuestMessagingOnboardingComponent } from 'src/shared/components/onboarding/guest-messaging-onboarding/guest-messaging-onboarding.component';
import { DirectBookingOnboardingComponent } from 'src/shared/components/onboarding/direct-booking-onboarding/direct-booking-onboarding.component';
import { ListingOptimizationOnboardingComponent } from 'src/shared/components/onboarding/listing-optimization-onboarding/listing-optimization-onboarding.component';
import { EditPricingPage } from 'src/pages/marketing/components/edit-pricing/edit-pricing.page';
import { GuidebookOnboardingComponent } from 'src/shared/components/onboarding/guidebook-onboarding/guidebook-onboarding.component';
import { ChecklistsOnboardingComponent } from 'src/shared/components/onboarding/checklists-onboarding/checklists-onboarding.component';

import { OnboardingCallComponent } from 'src/shared/components/onboarding/onboarding-call/onboarding-call.component';
import { GoLivePage } from 'src/pages/go-live/go-live.page';
import { EnableAutopilotComponent } from 'src/pages/go-live/enable-autopilot/enable-autopilot.component';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { SuccessPage } from 'src/shared/pages/success/success';
import { AddPropertyFormComponent } from 'src/shared/components/add-property-form/add-property-form.component';
import { environment } from 'src/environments/environment';

interface ConstellationNode {
  id: string;
  title: string;
  desc: string;
  icon: string;
  leftPx: number;
  topPx: number;
  svgPath: string;
  hovered: boolean;
  visible: boolean;
  floating: boolean;
  completed: boolean;
}

interface StarData {
  style: string;
}

interface ParticleData {
  style: string;
}

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './getting-started.page.html',
  styleUrls: ['./getting-started.page.scss'],
  encapsulation: ViewEncapsulation.None
})
export class GettingStartedPage implements OnInit, AfterViewInit, OnDestroy {
  windowService = inject(WindowService);
  private cdr = inject(ChangeDetectorRef);
  private client = inject(Client);
  private fb = inject(FormBuilder);
  private location = inject(Location);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private authModalService = inject(AuthModalService);
  private currentAddress = inject(CurrentAddress);
  private storage = inject(TidyStorage);
  private themeService = inject(ThemeService);
  private stripeProvider = inject(StripeProvider);
  private financialConnectionsService = inject(FinancialConnectionsService);
  private me = inject(Me);
  private subscriptionsQueries = inject(SubscriptionsQueriesService);
  private posthogService = inject(PosthogService);
  private renderer = inject(Renderer2);

  viewState: 'loading' | 'select_type' | 'short_term' | 'long_term' | 'waitlist_joined' | 'has_listing_question' | 'no_listing_schedule_call' | 'call_scheduled' = 'loading';
  addressesLoaded = false;
  addresses: AddressModel[] = [];
  selectedAddressId: number | null = null;
  selectedAddressStr = '';
  selectedAddressName = '';
  conciergeDropdownOpen = false;
  conciergeInfoOpen = false;
  addressSelectForm: FormGroup;
  addressSelectItems: SelectModel[] = [];
  nodes: ConstellationNode[] = [];
  mobileNodes: ConstellationNode[] = [];
  stars: StarData[] = [];
  particles: ParticleData[] = [];
  introStarted = false;
  trustStripVisible = false;
  svgViewBox = '0 0 0 0';
  centerBtnStyle = '';
  completedSteps = new Set<string>();
  showStartHere = false;
  startHereBelow = false;
  isLive = false;
  private backDestination = 'more';
  private hasLoadedOnce = false;
  private panelClosedSubscription: Subscription;
  private introTimeouts: ReturnType<typeof setTimeout>[] = [];
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private listingsPollingInterval: ReturnType<typeof setInterval> | null = null;

  @ViewChild('sceneContainer') sceneContainer: ElementRef;
  @ViewChild('inlineCalendlyContainer') inlineCalendlyContainer: ElementRef;

  callScheduled = false;
  private inlineCalendlyScript: HTMLScriptElement | null = null;
  private inlineCalendlyObserver: MutationObserver | null = null;
  private boundCalendlyListener = this.onCalendlyMessage.bind(this);

  private readonly CARD_WIDTH = 220;
  private readonly CARD_HEIGHT = 52;
  private readonly HEADER_ZONE = { width: 500, height: 170 };
  private readonly CENTER_BTN_RADIUS = 65;
  private readonly CARD_MIN_DISTANCE = 230;
  private readonly PADDING = 30;

  private readonly steps = [
    { id: 'onboarding-call', title: 'Free Setup Call', desc: 'Schedule a call with our team.', icon: 'assets/svg/call-outline.svg' },
    { id: 'bank', title: 'Connect Bank', desc: 'Link financials for payouts.', icon: 'assets/svg/cash-outline.svg' },
    { id: 'locks', title: 'Connect Locks', desc: 'Sync smart locks for auto-codes.', icon: 'assets/svg/lock-closed-outline.svg' },
    { id: 'legal', title: 'Legal / Compliance', desc: 'Permits and local regulations.', icon: 'assets/svg/shield-checkmark-outline.svg' },
    { id: 'pros', title: 'Add / Notify Pros', desc: 'Import and notify your pros.', icon: 'assets/svg/people-outline.svg' },
    // { id: 'messaging', title: 'Guest Messaging', desc: 'Automate inbox replies.', icon: 'assets/svg/chatbubble-outline.svg' },
    // { id: 'direct', title: 'Direct Booking', desc: 'Set up your direct website.', icon: 'assets/svg/calendar-outline.svg' },
    { id: 'listing-opt', title: 'Listing Optimization', desc: 'AI reviews your descriptions.', icon: 'assets/svg/analytics-outline.svg' },
    { id: 'pricing', title: 'Pricing Optimization', desc: 'Dynamic rates configuration.', icon: 'assets/svg/trending-up-outline.svg' },
    { id: 'checklists', title: 'Checklists', desc: 'Create turnover procedures.', icon: 'assets/svg/checkbox-outline.svg' },
    { id: 'guidebook', title: 'Digital Guidebook', desc: 'WiFi, house rules, and more.', icon: 'assets/svg/book-outline.svg' },
    { id: 'listings', title: 'Connect Listings', desc: 'Sync Airbnb & VRBO.', icon: 'assets/svg/home-outline.svg' }
  ];

  async ngOnInit() {
    this.themeService.forceDarkMode();
    this.addressSelectForm = this.fb.group({ selectedAddress: [''] });
    this.createStars();
    this.createParticles();
    await this.loadBackDestination();
    await this.loadAddresses();
  }

  // The entry point (Overview CTA vs the More page) saves where the user came
  // from so the mobile back button can return them there.
  private async loadBackDestination() {
    const savedBackUrl = await this.storage.retrieve('getting-started-back-url');
    this.backDestination = savedBackUrl === 'overview' ? 'overview' : 'more';
  }

  private async loadAddresses() {
    try {
      this.addresses = await this.client.getAddresses() || [];
      this.addressesLoaded = true;
      this.buildAddressSelectItems();
      if (!this.addresses.length) {
        this.goToAddProperty();
      }
      const savedAddressId = await this.storage.retrieve('getting-started-selected-address-id');
      if (savedAddressId && this.addresses.find(a => a.id === savedAddressId)) {
        this.selectAddress(this.addresses.find(a => a.id === savedAddressId)!);
      } else if (this.addresses.length > 0) {
        const globalId = this.currentAddress.addressId;
        const globalMatch = globalId ? this.addresses.find(a => String(a.id) === String(globalId)) : null;
        this.selectAddress(globalMatch || this.addresses[0]);
      }
      this.cdr.markForCheck();
    } catch (err) {
      this.addressesLoaded = true;
      this.cdr.markForCheck();
    }
  }

  private buildAddressSelectItems() {
    this.addressSelectItems = this.addresses.map(a => ({
      value: String(a.id),
      viewValue: a.address_name || a.address1 || ''
    }));
    this.cdr.markForCheck();
  }

  private updateSelectedAddressName() {
    const addr = this.addresses.find(a => a.id === this.selectedAddressId);
    this.selectedAddressName = addr?.address_name || addr?.address1 || '';
  }

  selectAddress = (address: AddressModel) => {
    this.selectedAddressId = address.id;
    this.selectedAddressStr = String(address.id);
    this.updateSelectedAddressName();
    this.storage.save('getting-started-selected-address-id', address.id);
    this.addressSelectForm.get('selectedAddress')!.setValue(String(address.id), { emitEvent: false });
    this.nodes = [];
    this.introStarted = false;
    this.trustStripVisible = false;
    this.isLive = false;
    this.introTimeouts.forEach(t => clearTimeout(t));
    this.introTimeouts = [];
    this.loadRentalType();
  };

  clearSelection = () => {
    this.selectedAddressId = null;
    this.selectedAddressStr = '';
    this.selectedAddressName = '';
    this.storage.save('getting-started-selected-address-id', null);
    this.viewState = 'loading';
    this.nodes = [];
    this.introStarted = false;
    this.isLive = false;
    this.conciergeDropdownOpen = false;
    this.stopPolling();
    this.stopListingsPolling();
    this.introTimeouts.forEach(t => clearTimeout(t));
    this.introTimeouts = [];
    this.addressSelectForm.get('selectedAddress')!.setValue('', { emitEvent: false });
    this.cdr.markForCheck();
  };

  ionViewWillEnter() {
    this.themeService.forceDarkMode();
    if (this.viewState === 'short_term') {
      this.loadCompletionStatus();
      this.startPolling();
    }
  }

  ionViewWillLeave() {
    this.stopPolling();
    this.stopListingsPolling();
  }

  private async loadCompletionStatus() {
    if (!this.selectedAddressId) {return;}
    try {
      const prefix = `external.onboarding_step.${this.selectedAddressId}.`;
      const keys = [
        ...this.steps.map(s => `${prefix}${s.id}`),
        `${prefix}go_live`,
        `${prefix}notify`
      ];
      const metadata: any[] = await this.client.getMetadata(keys);
      const metaMap = new Map<string, any>();
      if (Array.isArray(metadata)) {
        for (const item of metadata) {
          if (item?.key) {metaMap.set(this.stripExternalPrefix(item.key), item.value);}
        }
      }
      const previousCompleted = new Set(this.completedSteps);
      this.completedSteps = new Set<string>();
      for (const step of this.steps) {
        if (metaMap.get(`onboarding_step.${this.selectedAddressId}.${step.id}`) === 'completed') {
          this.completedSteps.add(step.id);
        }
      }
      if (metaMap.get(`onboarding_step.${this.selectedAddressId}.notify`) !== 'completed') {
        this.completedSteps.delete('pros');
      }
      for (const node of this.nodes) {
        node.completed = this.completedSteps.has(node.id);
      }
      const hasNewCompletion = [...this.completedSteps].some(id => !previousCompleted.has(id));
      if (this.hasLoadedOnce && hasNewCompletion) {
        this.throwConfetti();
      }
      this.hasLoadedOnce = true;
      const goLiveVal = metaMap.get(`onboarding_step.${this.selectedAddressId}.go_live`);
      this.isLive = goLiveVal === 'completed';
      this.showStartHere = !this.completedSteps.has('listings');
      this.cdr.markForCheck();
      this.trackNewCompletions();
      if (!this.completedSteps.has('listings')) {
        this.checkAndPollListings();
      } else {
        this.stopListingsPolling();
      }
    } catch (err) {
      // Non-fatal
    }
  }

  private async checkAndPollListings() {
    if (!this.selectedAddressId) {return;}
    try {
      const listings: any[] = await this.client.getAddressListings(this.selectedAddressId);
      if (listings?.length > 0) {
        const allActive = listings.every(l => l.status !== 'connecting');
        if (allActive) {
          await this.completeListingsStep();
        } else {
          this.startListingsPolling();
        }
      }
    } catch {
      // Non-fatal
    }
  }

  private startListingsPolling() {
    if (this.listingsPollingInterval) {return;}
    this.listingsPollingInterval = setInterval(() => this.pollListingsStatus(), 5000);
  }

  private stopListingsPolling() {
    if (this.listingsPollingInterval) {
      clearInterval(this.listingsPollingInterval);
      this.listingsPollingInterval = null;
    }
  }

  private async pollListingsStatus() {
    if (!this.selectedAddressId || this.completedSteps.has('listings')) {
      this.stopListingsPolling();
      return;
    }
    try {
      const listings: any[] = await this.client.getAddressListings(this.selectedAddressId);
      if (listings?.length > 0 && listings.every(l => l.status !== 'connecting')) {
        await this.completeListingsStep();
      }
    } catch {
      // Non-fatal
    }
  }

  private async completeListingsStep() {
    this.stopListingsPolling();
    try {
      await this.client.addMetadata({
        key: `onboarding_step.${this.selectedAddressId}.listings`,
        value: 'completed'
      });
      this.completedSteps.add('listings');
      this.showStartHere = false;
      for (const node of this.nodes) {
        if (node.id === 'listings') {node.completed = true;}
      }
      this.cdr.markForCheck();
      this.trackNewCompletions();
    } catch {
      // Non-fatal
    }
  }

  private trackNewCompletions(): void {
    if (!this.selectedAddressId) { return; }
    const storageKey = `posthog_onboarding_tracked_${this.selectedAddressId}`;
    let tracked: Set<string>;
    try {
      tracked = new Set<string>(JSON.parse(localStorage.getItem(storageKey) || '[]'));
    } catch {
      tracked = new Set<string>();
    }
    const allCompleted = new Set<string>(this.completedSteps);
    if (this.isLive) {
      allCompleted.add('go_live');
    }
    let hasNew = false;
    for (const stepId of allCompleted) {
      if (!tracked.has(stepId)) {
        const stepDef = this.steps.find(s => s.id === stepId);
        this.posthogService.capture('onboarding_step_completed', {
          step_id: stepId,
          step_title: stepDef?.title ?? (stepId === 'go_live' ? 'Go Live' : stepId),
          address_id: this.selectedAddressId,
          is_desktop: this.windowService.isDesktopRes,
          environment: environment.environment,
          completed_count: allCompleted.size,
          total_steps: this.steps.length,
        });
        tracked.add(stepId);
        hasNew = true;
      }
    }
    if (hasNew) {
      try {
        localStorage.setItem(storageKey, JSON.stringify([...tracked]));
      } catch {
        // localStorage write can fail in private mode or when quota is exceeded; safe to ignore
      }
    }
  }

  private startPolling() {
    this.stopPolling();
    this.pollingInterval = setInterval(() => this.loadCompletionStatus(), 10000);
  }

  private stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  ngAfterViewInit() {
    if (this.viewState === 'short_term') {
      if (this.windowService.isDesktopRes) {
        this.layoutScene();
      }
      this.triggerIntro();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (this.conciergeDropdownOpen && !target.closest('.gs-concierge-wrapper')) {
      this.conciergeDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.viewState !== 'short_term') {return;}
    if (this.resizeTimer) {clearTimeout(this.resizeTimer);}
    if (this.windowService.isDesktopRes) {
      this.resizeTimer = setTimeout(() => this.layoutScene(), 50);
    }
  }

  private layoutScene() {
    const el = this.sceneContainer?.nativeElement;
    if (!el) {return;}

    const w = el.clientWidth || window.innerWidth;
    const h = el.clientHeight || window.innerHeight;

    // Center shifted slightly right and down to account for header in top-left
    const centerX = w * 0.52;
    const centerY = h * 0.52;

    this.svgViewBox = `0 0 ${w} ${h}`;
    this.centerBtnStyle = `left: ${centerX}px; top: ${centerY}px;`;

    // Compute elliptical ring radii based on container size
    const minDim = Math.min(w, h);
    const innerRx = Math.max(w * 0.2, 200);
    const innerRy = Math.max(h * 0.2, 150);
    const outerRx = Math.max(w * 0.36, 340);
    const outerRy = Math.max(h * 0.36, 260);

    const positions: { x: number; y: number }[] = [];

    const nodeData = this.steps.map((step, index) => {
      const isInner = index < 5;
      const rx = isInner ? innerRx : outerRx;
      const ry = isInner ? innerRy : outerRy;
      const angleOffset = isInner ? 0 : (Math.PI / 7);
      const count = isInner ? 5 : 5;
      const idx = isInner ? index : index - 5;
      const angle = (idx / count) * 2 * Math.PI - Math.PI / 2 + angleOffset;

      const x = centerX + rx * Math.cos(angle);
      const y = centerY + ry * Math.sin(angle);

      return { step, x, y };
    });

    // Extract positions for relaxation
    const coords = nodeData.map(n => ({ x: n.x, y: n.y }));

    // Force-directed relaxation
    for (let iter = 0; iter < 60; iter++) {
      for (let i = 0; i < coords.length; i++) {
        let fx = 0, fy = 0;

        // Card-to-card repulsion
        for (let j = 0; j < coords.length; j++) {
          if (i === j) {continue;}
          const dx = coords[i].x - coords[j].x;
          const dy = coords[i].y - coords[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < this.CARD_MIN_DISTANCE && dist > 0) {
            const force = (this.CARD_MIN_DISTANCE - dist) * 0.3;
            fx += (dx / dist) * force;
            fy += (dy / dist) * force;
          }
        }

        // Header exclusion zone repulsion (top-left rectangle)
        const cardCenterX = coords[i].x;
        const cardCenterY = coords[i].y;
        const headerRight = this.HEADER_ZONE.width + this.PADDING;
        const headerBottom = this.HEADER_ZONE.height + this.PADDING;
        if (cardCenterX - this.CARD_WIDTH / 2 < headerRight && cardCenterY - this.CARD_HEIGHT / 2 < headerBottom) {
          // Push card right and/or down
          const overlapX = headerRight - (cardCenterX - this.CARD_WIDTH / 2);
          const overlapY = headerBottom - (cardCenterY - this.CARD_HEIGHT / 2);
          if (overlapX > 0 && overlapY > 0) {
            if (overlapX < overlapY) {
              fx += overlapX * 0.5;
            } else {
              fy += overlapY * 0.5;
            }
          }
        }

        // Center button repulsion
        const dcx = coords[i].x - centerX;
        const dcy = coords[i].y - centerY;
        const distToCenter = Math.sqrt(dcx * dcx + dcy * dcy);
        const minCenterDist = this.CENTER_BTN_RADIUS + this.CARD_WIDTH / 2 + 20;
        if (distToCenter < minCenterDist && distToCenter > 0) {
          const force = (minCenterDist - distToCenter) * 0.4;
          fx += (dcx / distToCenter) * force;
          fy += (dcy / distToCenter) * force;
        }

        // Boundary constraints
        const halfW = this.CARD_WIDTH / 2;
        const halfH = this.CARD_HEIGHT / 2;
        if (coords[i].x - halfW < this.PADDING) {
          fx += (this.PADDING - (coords[i].x - halfW)) * 0.5;
        }
        if (coords[i].x + halfW > w - this.PADDING) {
          fx -= ((coords[i].x + halfW) - (w - this.PADDING)) * 0.5;
        }
        if (coords[i].y - halfH < this.PADDING) {
          fy += (this.PADDING - (coords[i].y - halfH)) * 0.5;
        }
        if (coords[i].y + halfH > h - this.PADDING) {
          fy -= ((coords[i].y + halfH) - (h - this.PADDING)) * 0.5;
        }

        coords[i].x += fx;
        coords[i].y += fy;
      }
    }

    // Clamp final positions to bounds
    for (const c of coords) {
      c.x = Math.max(this.CARD_WIDTH / 2 + this.PADDING, Math.min(w - this.CARD_WIDTH / 2 - this.PADDING, c.x));
      c.y = Math.max(this.CARD_HEIGHT / 2 + this.PADDING, Math.min(h - this.CARD_HEIGHT / 2 - this.PADDING, c.y));
    }

    // Build or update nodes
    const preserveState = this.nodes.length === this.steps.length;
    this.nodes = nodeData.map((nd, i) => {
      const tx = coords[i].x;
      const ty = coords[i].y;

      const angleToCard = Math.atan2(ty - centerY, tx - centerX);
      const lineEndX = tx - Math.cos(angleToCard) * 10;
      const lineEndY = ty - Math.sin(angleToCard) * 10;
      const cp1X = centerX + (lineEndX - centerX) * 0.4;
      const cp1Y = centerY + (lineEndY - centerY) * 0.1;
      const svgPath = `M ${centerX} ${centerY} Q ${cp1X} ${cp1Y}, ${lineEndX} ${lineEndY}`;

      return {
        ...nd.step,
        leftPx: tx - this.CARD_WIDTH / 2,
        topPx: ty - this.CARD_HEIGHT / 2,
        svgPath,
        hovered: preserveState ? this.nodes[i].hovered : false,
        visible: preserveState ? this.nodes[i].visible : false,
        floating: preserveState ? this.nodes[i].floating : false,
        completed: this.completedSteps.has(nd.step.id)
      };
    });

    const listingsNode = this.nodes.find(n => n.id === 'listings');
    this.startHereBelow = listingsNode ? listingsNode.leftPx < 120 : false;

    this.cdr.markForCheck();
  }

  private createStars() {
    this.stars = Array.from({ length: 120 }, () => {
      const size = Math.random() * 2 + 1;
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const duration = 3 + Math.random() * 5;
      const maxOpacity = 0.2 + Math.random() * 0.4;
      const delay = Math.random() * 5;
      return {
        style: `width: ${size}px; height: ${size}px; left: ${left}%; top: ${top}%; --gs-duration: ${duration}s; --gs-max-opacity: ${maxOpacity}; animation-delay: ${delay}s;`
      };
    });
  }

  private createParticles() {
    this.particles = Array.from({ length: 40 }, () => {
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const duration = 8 + Math.random() * 12;
      const delay = Math.random() * 5;
      const drift = (Math.random() - 0.5) * 150;
      return {
        style: `left: ${left}%; top: ${top}%; --gs-particle-drift: ${drift}px; animation: gsAmbientFloat ${duration}s ${delay}s infinite linear;`
      };
    });
  }

  private initNodes() {
    if (!this.windowService.isDesktopRes && this.nodes.length === 0) {
      this.nodes = this.steps.map(step => ({
        ...step,
        leftPx: 0,
        topPx: 0,
        svgPath: '',
        hovered: false,
        visible: false,
        floating: false,
        completed: this.completedSteps.has(step.id)
      }));
      const listings = this.nodes.find(n => n.id === 'listings');
      this.mobileNodes = listings
        ? [listings, ...this.nodes.filter(n => n.id !== 'listings')]
        : this.nodes;
    }
  }

  private triggerIntro() {
    const t1 = setTimeout(() => {
      this.introStarted = true;
      this.cdr.markForCheck();
    }, 200);
    this.introTimeouts.push(t1);

    const activeNodes = this.windowService.isDesktopRes ? this.nodes : this.mobileNodes;
    const order = Array.from({ length: activeNodes.length }, (_, i) => i);
    if (this.windowService.isDesktopRes) {
      order.sort(() => Math.random() - 0.5);
    }

    order.forEach((idx, i) => {
      const t = setTimeout(() => {
        activeNodes[idx].visible = true;
        this.cdr.markForCheck();
        const t2 = setTimeout(() => {
          activeNodes[idx].floating = true;
          this.cdr.markForCheck();
        }, 1200);
        this.introTimeouts.push(t2);
      }, 600 + (i * 80));
      this.introTimeouts.push(t);
    });

    const lastNodeDelay = 600 + (order.length - 1) * 80;
    const tStrip = setTimeout(() => {
      this.trustStripVisible = true;
      this.cdr.markForCheck();
    }, lastNodeDelay + 400);
    this.introTimeouts.push(tStrip);
  }

  private async loadRentalType() {
    if (!this.selectedAddressId) {
      this.viewState = 'select_type';
      this.cdr.markForCheck();
      return;
    }
    try {
      const prefix = `external.onboarding_step.${this.selectedAddressId}.`;
      const allKeys = [
        `${prefix}rental_type`,
        `${prefix}waitlist`,
        `${prefix}go_live`,
        `${prefix}notify`,
        `${prefix}has_listing`,
        `${prefix}concierge_call_scheduled`,
        ...this.steps.map(s => `${prefix}${s.id}`)
      ];
      const metadata: any[] = await this.client.getMetadata(allKeys);
      const metaMap = new Map<string, any>();
      if (Array.isArray(metadata)) {
        for (const item of metadata) {
          if (item?.key) {metaMap.set(this.stripExternalPrefix(item.key), item.value);}
        }
      }

      const rentalType = metaMap.get(`onboarding_step.${this.selectedAddressId}.rental_type`);

      if (rentalType === 'short_term') {
        const hasListing = metaMap.get(`onboarding_step.${this.selectedAddressId}.has_listing`);

        if (!hasListing) {
          this.viewState = 'has_listing_question';
          this.cdr.markForCheck();
          return;
        }

        if (hasListing === 'no') {
          const conciergeCall = metaMap.get(`onboarding_step.${this.selectedAddressId}.concierge_call_scheduled`);
          if (conciergeCall) {
            this.callScheduled = true;
            this.viewState = 'call_scheduled';
          } else {
            this.viewState = 'no_listing_schedule_call';
            setTimeout(() => this.loadInlineCalendly(), 0);
          }
          this.cdr.markForCheck();
          return;
        }

        this.viewState = 'short_term';
        const goLiveVal = metaMap.get(`onboarding_step.${this.selectedAddressId}.go_live`);
        this.isLive = goLiveVal === 'completed';
        this.completedSteps = new Set<string>();
        for (const step of this.steps) {
          if (metaMap.get(`onboarding_step.${this.selectedAddressId}.${step.id}`) === 'completed') {
            this.completedSteps.add(step.id);
          }
        }
        if (metaMap.get(`onboarding_step.${this.selectedAddressId}.notify`) !== 'completed') {
          this.completedSteps.delete('pros');
        }
        for (const node of this.nodes) {
          node.completed = this.completedSteps.has(node.id);
        }
        this.showStartHere = !this.completedSteps.has('listings');
        this.trackNewCompletions();
        this.initNodes();
        this.cdr.markForCheck();
        setTimeout(() => {
          if (this.windowService.isDesktopRes) {this.layoutScene();}
          this.triggerIntro();
        }, 0);
        this.startPolling();
        return;
      } else if (rentalType === 'long_term') {
        const waitlist = metaMap.get(`onboarding_step.${this.selectedAddressId}.waitlist`);
        this.viewState = waitlist === 'joined' ? 'waitlist_joined' : 'long_term';
        this.cdr.markForCheck();
        return;
      }

      this.viewState = 'select_type';
      this.cdr.markForCheck();
    } catch (err) {
      this.viewState = 'select_type';
      this.cdr.markForCheck();
    }
  }

  async selectRentalType(type: 'short_term' | 'long_term') {
    if (this.selectedAddressId) {
      try {
        await this.client.addMetadata({
          key: `onboarding_step.${this.selectedAddressId}.rental_type`,
          value: type
        });
        if (type === 'short_term') {
          await this.client.addMetadata({
            key: `onboarding_step.${this.selectedAddressId}.waitlist`,
            value: 'false'
          });
        }
      } catch (err) {
        // Non-fatal
      }
    }

    if (type === 'short_term') {
      this.viewState = 'has_listing_question';
      this.cdr.markForCheck();
    } else {
      this.viewState = type;
      this.cdr.markForCheck();
    }
  }

  async joinWaitlist() {
    if (this.selectedAddressId) {
      try {
        await this.client.addMetadata({
          key: `onboarding_step.${this.selectedAddressId}.waitlist`,
          value: 'joined'
        });
      } catch (err) {
        // Non-fatal
      }
    }
    this.viewState = 'waitlist_joined';
    this.cdr.markForCheck();
  }

  leaveWaitlist() {
    this.viewState = 'select_type';
    this.cdr.markForCheck();
  }

  async selectHasListing(answer: 'yes' | 'no') {
    if (this.selectedAddressId) {
      try {
        await this.client.addMetadata({
          key: `onboarding_step.${this.selectedAddressId}.has_listing`,
          value: answer
        });
      } catch {
        // Non-fatal
      }
    }

    if (answer === 'yes') {
      this.viewState = 'short_term';
      this.cdr.markForCheck();
      await this.loadCompletionStatus();
      this.initNodes();
      this.cdr.markForCheck();
      setTimeout(() => {
        if (this.windowService.isDesktopRes) {
          this.layoutScene();
        }
        this.triggerIntro();
      }, 0);
      this.startPolling();
    } else {
      this.viewState = 'no_listing_schedule_call';
      this.cdr.markForCheck();
      setTimeout(() => this.loadInlineCalendly(), 0);
    }
  }

  goBackFromListingQuestion() {
    this.viewState = 'select_type';
    if (this.selectedAddressId) {
      this.client.addMetadata({
        key: `onboarding_step.${this.selectedAddressId}.rental_type`,
        value: ''
      }).catch(() => {
        // Non-fatal
      });
    }
    this.cdr.markForCheck();
  }

  goBackFromNoListing() {
    this.destroyInlineCalendly();
    this.viewState = 'has_listing_question';
    this.cdr.markForCheck();
  }

  private loadInlineCalendly() {
    if (this.inlineCalendlyScript) { return; }
    this.inlineCalendlyScript = document.createElement('script');
    this.inlineCalendlyScript.src = 'https://assets.calendly.com/assets/external/widget.js';
    this.inlineCalendlyScript.async = true;
    document.head.appendChild(this.inlineCalendlyScript);
    this.watchForInlineCalendlyIframe();
    window.addEventListener('message', this.boundCalendlyListener);
  }

  private watchForInlineCalendlyIframe() {
    const container = this.inlineCalendlyContainer?.nativeElement;
    if (!container) { return; }

    this.inlineCalendlyObserver = new MutationObserver(() => {
      const iframe = container.querySelector('iframe') as HTMLIFrameElement;
      if (iframe) {
        iframe.style.height = '100%';
        this.inlineCalendlyObserver?.disconnect();
      }
    });
    this.inlineCalendlyObserver.observe(container, { childList: true, subtree: true });
  }

  private destroyInlineCalendly() {
    this.inlineCalendlyObserver?.disconnect();
    this.inlineCalendlyObserver = null;
    window.removeEventListener('message', this.boundCalendlyListener);
    if (this.inlineCalendlyScript) {
      document.head.removeChild(this.inlineCalendlyScript);
      this.inlineCalendlyScript = null;
    }
  }

  private onCalendlyMessage(event: MessageEvent) {
    if (!event.data?.event) { return; }
    if (event.data.event === 'calendly.event_scheduled') {
      this.onInlineCallScheduled();
    }
  }

  private async onInlineCallScheduled() {
    this.callScheduled = true;
    this.viewState = 'call_scheduled';
    this.destroyInlineCalendly();
    this.cdr.markForCheck();

    if (this.selectedAddressId) {
      try {
        await this.client.addMetadata({
          key: `onboarding_step.${this.selectedAddressId}.concierge_call_scheduled`,
          value: 'true'
        });
      } catch {
        // Non-fatal
      }
    }
  }

  goBack() {
    this.storage.save('getting-started-selected-address-id', null);
    this.storage.save('getting-started-back-url', null);
    if (this.windowService.isDesktopRes) {
      this.navCtrl.navigateForward('properties');
    } else {
      this.navCtrl.navigateForward(this.backDestination);
    }
  }

  handleMobileBack = (): boolean => {
    this.storage.save('getting-started-selected-address-id', null);
    this.storage.save('getting-started-back-url', null);
    this.navCtrl.navigateForward(this.backDestination);
    return false;
  };

  onAddressSelectChange = (value: string) => {
    const address = this.addresses.find(a => String(a.id) === value);
    if (address) {
      this.selectAddress(address);
    }
  };

  toggleConciergeDropdown = () => {
    this.conciergeDropdownOpen = !this.conciergeDropdownOpen;
    this.cdr.markForCheck();
  };

  openConciergeInfo = () => {
    this.conciergeInfoOpen = true;
    this.conciergeDropdownOpen = false;
    this.cdr.markForCheck();
  };

  closeConciergeInfo = () => {
    this.conciergeInfoOpen = false;
    this.cdr.markForCheck();
  };

  scheduleConciergeCall = () => {
    this.conciergeDropdownOpen = false;
    this.cdr.markForCheck();
  };

  goToAddProperty = () => {
    this.rightSidePanelService.openRightSidePanel(AddPropertyFormComponent, {
      smallDisplay: true,
      showImportButton: false,
      onPropertyAdded: async (address: any) => {
        this.currentAddress.addressId = address.id;
        localStorage.setItem('region_id', address.region_id);
        const freshAddresses = [...this.addresses, address];
        await this.storage.save('addresses', freshAddresses);
        localStorage.setItem('hasAtLeastOneAddress', 'true');
        localStorage.setItem('hasMoreThanOneAddress', freshAddresses.length > 1 ? 'true' : 'false');
        localStorage.setItem('getAddressesFromBackend', 'true');
        this.addresses = freshAddresses;
        this.buildAddressSelectItems();
        const newAddr = freshAddresses.find(a => a.id === address.id);
        if (newAddr) {
          this.selectAddress(newAddr);
        }
      }
    });
    this.rightSidePanelService.afterPanelClosed().pipe(take(1)).subscribe(() => {
      if (!this.addresses.length) {
        this.location.back();
      }
      this.cdr.markForCheck();
    });
  };

  async goLive() {
    const params = { addressId: this.selectedAddressId, fromGettingStarted: true };
    this.rightSidePanelService.setDialogPageTitle('Go Live');
    await this.rightSidePanelService.navigateTo('go-live', params, GoLivePage);
    this.panelClosedSubscription?.unsubscribe();
    this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(() => {
      this.loadCompletionStatus();
    });
  }


  private readonly SUBSCRIPTION_GATED_STEPS = ['listing-opt', 'direct', 'legal', 'pricing', 'pros'];

  private readonly stepRoutes: Record<string, { route: string; component: any; title: string }> = {
    'onboarding-call': { route: 'getting-started/onboarding-call', component: OnboardingCallComponent, title: 'Free Setup Call' },
    'listings': { route: 'listings-setup', component: ListingsSetupComponent, title: 'Tell Us About Your Listings' },
    'bank': { route: 'getting-started/connect-bank', component: ConnectBankOnboardingComponent, title: 'Connect Bank' },
    'locks': { route: 'smart-locks/manage-assignments', component: ManageLockAssignmentsComponent, title: 'Connect Locks' },
    'legal': { route: 'getting-started/legal-compliance', component: LegalComplianceOnboardingComponent, title: 'Legal / Compliance' },
    'pros': { route: 'onboarding/addPros', component: AddProsOnboardingComponent, title: 'Add / Notify Pros' },
    'messaging': { route: 'getting-started/guest-messaging', component: GuestMessagingOnboardingComponent, title: 'Guest Messaging' },
    'direct': { route: 'getting-started/direct-booking', component: DirectBookingOnboardingComponent, title: 'Direct Booking' },
    'listing-opt': { route: 'getting-started/listing-optimization', component: ListingOptimizationOnboardingComponent, title: 'Listing Optimization' },
    'pricing': { route: 'edit-pricing', component: EditPricingPage, title: 'Pricing Optimization' },
    'checklists': { route: 'getting-started/checklists', component: ChecklistsOnboardingComponent, title: 'Checklists' },
    'guidebook': { route: 'getting-started/guidebook', component: GuidebookOnboardingComponent, title: 'Digital Guidebook' }
  };

  async onNodeClick(node: ConstellationNode) {
    const isGuestAccount = await this.storage.retrieve('isGuestAccount');
    if (isGuestAccount) {
      await this.authModalService.openAuthModal('auth', 'signup', 'Create Account to Get Started');
      const stillGuest = await this.storage.retrieve('isGuestAccount');
      if (stillGuest) { return; }
    }

    if (node.id === 'pricing') {
      const listings: any[] = await this.client.getAddressListings(this.selectedAddressId);
      if (!listings?.length) {
        const successParams: any = {
          header: 'Please connect your listing before optimizing your pricing',
          body: '',
          buttonText: 'Connect Listings',
          customActionKey: 'navigateToConnectListings',
          addressId: this.selectedAddressId,
          allButtonsPrimary: true,
          fromGettingStarted: true,
        };
        this.rightSidePanelService.setDialogPageTitle('Pricing Optimization');
        await this.rightSidePanelService.navigateTo('success', successParams, SuccessPage);
        this.panelClosedSubscription?.unsubscribe();
        this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(() => {
          this.loadCompletionStatus();
        });
        return;
      }
    }

    if (node.id === 'listing-opt') {
      const listings: any[] = await this.client.getAddressListings(this.selectedAddressId);
      if (!listings?.length) {
        const successParams: any = {
          header: 'Please connect your listing before optimizing your listing',
          body: '',
          buttonText: 'Connect Listings',
          customActionKey: 'navigateToConnectListings',
          addressId: this.selectedAddressId,
          allButtonsPrimary: true,
          fromGettingStarted: true,
        };

        this.rightSidePanelService.setDialogPageTitle('Listing Optimization');
        await this.rightSidePanelService.navigateTo('success', successParams, SuccessPage);
        this.panelClosedSubscription?.unsubscribe();
        this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(() => {
          this.loadCompletionStatus();
        });
        return;
      }
    }

    const stepConfig = this.stepRoutes[node.id];
    if (!stepConfig) {return;}

    const params: any = { addressId: this.selectedAddressId, fromGettingStarted: true };
    if (node.id === 'pros') {
      params.flow = 'addPros';
      params.combinedFlow = true;
      await this.storage.save('cameFromOnboarding', true);
      await this.storage.save('onboardingFlow', 'addPros');
    }

    if (this.SUBSCRIPTION_GATED_STEPS.includes(node.id)) {
      const subscriptions = await this.subscriptionsQueries.getSubscriptions();
      const currentSubscription = this.me.getCurrentSubscription(subscriptions);
      const hasAutopilot = currentSubscription?.plan?.key?.includes('full-autopilot');
      if (!hasAutopilot) {
        await this.storage.save('autopilot-pending-step', {
          route: stepConfig.route,
          title: stepConfig.title,
          params
        });
        this.rightSidePanelService.setDialogPageTitle('Enable Autopilot');
        await this.rightSidePanelService.navigateTo('go-live/enable-autopilot', { fromGettingStarted: true }, EnableAutopilotComponent);
        this.panelClosedSubscription?.unsubscribe();
        this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(() => {
          this.loadCompletionStatus();
        });
        return;
      }
    }

    if (node.id === 'listings') {
      this.stopPolling();
      this.stopListingsPolling();
    }

    this.rightSidePanelService.setDialogPageTitle(stepConfig.title);
    await this.rightSidePanelService.navigateTo(stepConfig.route, params, stepConfig.component);
    this.panelClosedSubscription?.unsubscribe();
    this.panelClosedSubscription = this.rightSidePanelService.afterPanelClosed().pipe(take(1)).subscribe(() => {
      this.loadCompletionStatus();
      this.startPolling();
    });
  }

  async throwConfetti() {
    const canvas = this.renderer.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';
    this.renderer.appendChild(document.body, canvas);
    const confettiAnimation = confetti.create(canvas, { resize: true });
    try {
      await confettiAnimation(spookyConfetti({
        particleCount: 150,
        spread: 180,
        startVelocity: 45,
        decay: 0.9,
        gravity: 1,
        origin: { x: 0.5, y: 0.5 },
      }));
    } catch (_error) {
      // Silent error - confetti animation failure is not critical
    }
    this.renderer.removeChild(document.body, canvas);
  }

  private stripExternalPrefix(key: string): string {
    return key.startsWith('external.') ? key.slice(9) : key;
  }

  ngOnDestroy() {
    this.stopPolling();
    this.stopListingsPolling();
    this.destroyInlineCalendly();
    this.introTimeouts.forEach(t => clearTimeout(t));
    if (this.resizeTimer) {clearTimeout(this.resizeTimer);}
    this.panelClosedSubscription?.unsubscribe();
  }
}
